import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { webPortalTarget } from '../platforms/web/desktop/webPortalTarget';
import { Activity, CalendarDays, HeartPulse, Plus, RefreshCw, X } from 'lucide-react';
import { healthModulesApi } from '../api';
import type { VitalRecordKind } from './HealthRecordDialog';
import './VitalHistoryDialog.css';

type RangeDays = 7 | 30 | 90;

interface Props {
  initialKind: VitalRecordKind;
  onClose: () => void;
  onRecord: (kind: VitalRecordKind) => void;
}

interface TrendSeries {
  name: string;
  color: string;
  values: number[];
}

function formatDate(value: string, includeTime = true) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  const dateText = date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
  if (!includeTime) return dateText;
  return `${dateText} ${date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
}

function average(values: number[]) {
  if (!values.length) return '--';
  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
}

function TrendChart({ series, dates, label }: { series: TrendSeries[]; dates: string[]; label: string }) {
  const width = 340;
  const height = 164;
  const padding = { left: 30, right: 12, top: 16, bottom: 28 };
  const allValues = series.flatMap((item) => item.values).filter(Number.isFinite);
  if (allValues.length < 2) return <div className="vhd-chart-empty">至少记录两次后显示趋势</div>;
  const minRaw = Math.min(...allValues);
  const maxRaw = Math.max(...allValues);
  const margin = Math.max(4, Math.round((maxRaw - minRaw) * 0.18));
  const min = Math.max(0, minRaw - margin);
  const max = maxRaw + margin;
  const spread = max - min || 1;
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const xFor = (index: number, length: number) => padding.left + (length <= 1 ? plotWidth / 2 : (index / (length - 1)) * plotWidth);
  const yFor = (value: number) => padding.top + plotHeight - ((value - min) / spread) * plotHeight;
  const ticks = [max, Math.round((max + min) / 2), min];
  return (
    <div className="vhd-chart-wrap">
      <svg className="vhd-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
        {ticks.map((tick, index) => {
          const y = padding.top + (index / (ticks.length - 1)) * plotHeight;
          return <g key={tick + index}><line x1={padding.left} x2={width - padding.right} y1={y} y2={y} /><text x={padding.left - 7} y={y + 3}>{tick}</text></g>;
        })}
        {series.map((item) => {
          const points = item.values.map((value, index) => `${xFor(index, item.values.length)},${yFor(value)}`).join(' ');
          return <g key={item.name}><polyline className="vhd-series" style={{ stroke: item.color }} points={points} vectorEffect="non-scaling-stroke" />{item.values.map((value, index) => <circle key={index} cx={xFor(index, item.values.length)} cy={yFor(value)} r="2.8" style={{ fill: item.color }} />)}</g>;
        })}
        {dates.length > 0 && <><text className="date" x={padding.left} y={height - 7}>{formatDate(dates[0], false)}</text><text className="date end" x={width - padding.right} y={height - 7}>{formatDate(dates[dates.length - 1], false)}</text></>}
      </svg>
      <div className="vhd-legend">{series.map((item) => <span key={item.name}><i style={{ background: item.color }} />{item.name}</span>)}</div>
    </div>
  );
}

export default function VitalHistoryDialog({ initialKind, onClose, onRecord }: Props) {
  const [kind, setKind] = useState<VitalRecordKind>(initialKind);
  const [days, setDays] = useState<RangeDays>(30);
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const dialogRef = useRef<HTMLElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = kind === 'bp' ? await healthModulesApi.getBpRecords(days) : await healthModulesApi.getHrRecords(days);
      setRecords(Array.isArray(response.data) ? response.data : []);
    } catch (requestError: any) {
      setRecords([]);
      setError(requestError?.response?.data?.detail || '历史记录加载失败');
    } finally {
      setLoading(false);
    }
  }, [days, kind]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setKind(initialKind); }, [initialKind]);
  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const timer = window.setTimeout(() => dialogRef.current?.querySelector<HTMLElement>('button')?.focus(), 0);
    const handleKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => { window.clearTimeout(timer); document.removeEventListener('keydown', handleKeyDown); document.body.style.overflow = previousOverflow; previousFocus?.focus?.(); };
  }, [onClose]);

  const ordered = useMemo(() => [...records].sort((a, b) => new Date(a.measured_at).getTime() - new Date(b.measured_at).getTime()), [records]);
  const latest = records[0];
  const chartSeries = useMemo<TrendSeries[]>(() => kind === 'bp'
    ? [
        { name: '收缩压', color: '#38bdf8', values: ordered.map((record) => Number(record.systolic)).filter(Number.isFinite) },
        { name: '舒张压', color: '#a78bfa', values: ordered.map((record) => Number(record.diastolic)).filter(Number.isFinite) },
      ]
    : [{ name: '心率', color: '#fb7185', values: ordered.map((record) => Number(record.heart_rate)).filter(Number.isFinite) }], [kind, ordered]);

  const bpSystolic = records.map((record) => Number(record.systolic)).filter(Number.isFinite);
  const bpDiastolic = records.map((record) => Number(record.diastolic)).filter(Number.isFinite);
  const heartRates = records.map((record) => Number(record.heart_rate)).filter(Number.isFinite);

  return createPortal((
    <div className="vhd-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section ref={dialogRef} className="vhd-dialog" role="dialog" aria-modal="true" aria-labelledby="vhd-title">
        <header className="vhd-header">
          <div><span>健康记录</span><h2 id="vhd-title">历史趋势</h2></div>
          <button type="button" onClick={onClose} aria-label="关闭"><X size={19} /></button>
        </header>

        <div className="vhd-toolbar">
          <div className="vhd-kind-tabs" role="tablist" aria-label="指标类型">
            <button type="button" role="tab" aria-selected={kind === 'bp'} className={kind === 'bp' ? 'active' : ''} onClick={() => setKind('bp')}><HeartPulse size={16} />血压</button>
            <button type="button" role="tab" aria-selected={kind === 'hr'} className={kind === 'hr' ? 'active' : ''} onClick={() => setKind('hr')}><Activity size={16} />心率</button>
          </div>
          <div className="vhd-range" aria-label="查询时间范围">{([7, 30, 90] as RangeDays[]).map((range) => <button key={range} type="button" className={days === range ? 'active' : ''} onClick={() => setDays(range)}>{range}天</button>)}</div>
        </div>

        <main className="vhd-content">
          {loading ? <div className="vhd-state"><span className="vhd-loader" />正在加载记录…</div> : error ? <div className="vhd-state error"><p>{error}</p><button type="button" onClick={load}><RefreshCw size={14} />重试</button></div> : records.length === 0 ? <div className="vhd-state empty"><CalendarDays size={30} /><strong>这段时间还没有{kind === 'bp' ? '血压' : '心率'}记录</strong><p>完成两次记录后即可查看变化趋势。</p><button type="button" onClick={() => onRecord(kind)}><Plus size={15} />立即记录</button></div> : <>
            <section className="vhd-summary" aria-label={`${days}天摘要`}>
              <div><span>最新</span><strong>{kind === 'bp' ? `${latest.systolic}/${latest.diastolic}` : latest.heart_rate}</strong><small>{kind === 'bp' ? 'mmHg' : '次/分'}</small></div>
              <div><span>{days}天平均</span><strong>{kind === 'bp' ? `${average(bpSystolic)}/${average(bpDiastolic)}` : average(heartRates)}</strong><small>{kind === 'bp' ? 'mmHg' : '次/分'}</small></div>
              <div><span>记录次数</span><strong>{records.length}</strong><small>次</small></div>
            </section>

            <section className="vhd-panel">
              <header><div><span>变化趋势</span><h3>{kind === 'bp' ? '血压曲线' : '心率曲线'}</h3></div><small>按测量时间</small></header>
              <TrendChart series={chartSeries} dates={ordered.map((record) => record.measured_at)} label={`${days}天${kind === 'bp' ? '收缩压和舒张压' : '心率'}变化趋势`} />
            </section>

            <section className="vhd-panel history">
              <header><div><span>明细记录</span><h3>最近 {records.length} 次</h3></div><button type="button" onClick={() => onRecord(kind)}><Plus size={14} />新增</button></header>
              <div className="vhd-list">{records.map((record) => <article key={`${kind}-${record.id}`}>
                <div className={`vhd-record-icon ${kind}`}>{kind === 'bp' ? <HeartPulse size={17} /> : <Activity size={17} />}</div>
                <div className="vhd-record-main"><strong>{kind === 'bp' ? `${record.systolic}/${record.diastolic}` : record.heart_rate}<small>{kind === 'bp' ? ' mmHg' : ' 次/分'}</small></strong><span>{formatDate(record.measured_at)} · {kind === 'bp' ? record.period_label : record.state_label}</span>{record.note && <p>{record.note}</p>}</div>
                <i className={record.status === 'normal' ? 'normal' : ''}>{record.status_label}</i>
              </article>)}</div>
            </section>
          </>}
        </main>
      </section>
    </div>
  ), webPortalTarget());
}
